/**
 * authSession — one place that knows "the facilitator's sign-in expired".
 *
 * 2026-09-29 UI audit: every data connection used to swallow a 401 on
 * /facilitator pages, so an expired sign-in left the portal open with
 * every tab failing and raw "Token has expired." toasts piling up.
 *
 * Now each connection calls notifyAuthExpired() on a 401 for a request
 * that carried a token. The portal listens and signs the facilitator out
 * with a friendly message, EXCEPT while a live drill console is open:
 * then it only warns, and signs out once the console closes, so a drill
 * is never cut off mid-session.
 */
type Listener = (info: { duringLiveDrill: boolean }) => void;

const listeners = new Set<Listener>();
let liveDrillOpen = false;
let pendingSignOut = false;
let lastNotified = 0;

export function onAuthExpired(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function notifyAuthExpired(): void {
  const now = Date.now();
  if (now - lastNotified < 3000) return; // many requests fail at once
  lastNotified = now;
  if (liveDrillOpen) pendingSignOut = true;
  listeners.forEach((l) => l({ duringLiveDrill: liveDrillOpen }));
}

const liveDrillWatchers = new Set<() => void>();

/** Whether the drill console is open now (no pop-ups there, lib/notices). */
export function isLiveDrillOpen(): boolean {
  return liveDrillOpen;
}

export function onLiveDrillOpenChange(watcher: () => void): () => void {
  liveDrillWatchers.add(watcher);
  return () => { liveDrillWatchers.delete(watcher); };
}

/** WorkshopConsole calls this on open (true) and close (false). */
export function setLiveDrillOpen(open: boolean): void {
  liveDrillOpen = open;
  liveDrillWatchers.forEach((w) => w());
  if (!open && pendingSignOut) {
    pendingSignOut = false;
    listeners.forEach((l) => l({ duringLiveDrill: false }));
  }
}

/** True when this request carried a sign-in token (so a 401 means "expired", not "wrong password"). */
export function requestHadToken(headers: unknown): boolean {
  const h = headers as Record<string, unknown> | undefined;
  const auth = h?.Authorization ?? h?.authorization;
  return typeof auth === 'string' && auth.startsWith('Bearer ');
}
